import { useCallback, useContext } from 'react';
import streamSaver from 'streamsaver';
import { useMsalAccessToken } from 'auth/useMsalAccessToken';
import { env } from 'config/env';
import { useTranslation } from 'react-i18next';
import { SearchColumnsVisibility } from 'stores/interfaces/SearchColumnsState';
import { LastQueriesContext } from 'config/urql/UrqlProvider';

interface DownloadResultQueriesProps {
  searchColumnsVisibility: SearchColumnsVisibility;
  showToastMessage: (message: string) => void;
}
/**
 * Custom hook for handling query download results.
 * @param props - {@link DownloadResultQueriesProps}
 * @returns Object containing a method to trigger download results
 */
export const useDownloadResultQueries = (props: DownloadResultQueriesProps) => {
  const { searchColumnsVisibility, showToastMessage } = props;
  const { t } = useTranslation();
  const { accessToken } = useMsalAccessToken();
  const lastExecutedQueries = useContext(LastQueriesContext);
  let currentAbortController: AbortController | null = null;

  const cancelPreviousDownload = () => {
    try {
      if (currentAbortController) {
        currentAbortController.abort();
      }
    } catch (error) {}
  };

  const startDownload = (): Promise<Response> => {
    cancelPreviousDownload();
    currentAbortController = new AbortController();
    const config = {
      viewColumns: searchColumnsVisibility,
      graphQlQuery: lastExecutedQueries['GetQueriesPage']
    };

    return fetch(env?.REACT_APP_API_URL?.replace('/graphql', '/queries/download') as string, {
      signal: currentAbortController.signal,
      method: 'POST',
      body: JSON.stringify(config),
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    });
  };

  const downloadFileContent = async (readable: ReadableStream) => {
    let writer: WritableStreamDefaultWriter | null = null;
    let reader: ReadableStreamDefaultReader | null = null;

    try {
      const fileStream = streamSaver.createWriteStream(`Queries_${new Date().toISOString()}.xlsx`);
      writer = fileStream.getWriter();
      reader = readable.getReader();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        await writer.write(value);
      }

      await writer.close();
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') {
        showToastMessage(t('manage-queries-download-error-toast-message'));
      }
    } finally {
      writer?.abort();
      writer?.releaseLock();

      reader?.cancel();
      reader?.releaseLock();
    }
  };

  const onDownloadResults = useCallback(async () => {
    try {
      showToastMessage(t('manage-queries-download-started-toast-message'));
      const response = await startDownload();

      if (!response.ok || !response.body) {
        throw new Error('Network response was not ok');
      }

      await downloadFileContent(response.body);
    } catch (error) {
      if (currentAbortController && !currentAbortController?.signal.aborted) {
        currentAbortController?.abort();
      }
      showToastMessage(t('manage-queries-download-error-toast-message'));
    }
  }, [accessToken, searchColumnsVisibility]);

  return {
    onDownloadResults
  };
};
